import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import TenantLogo from "@/components/TenantLogo";
import { useTenant } from "./TenantRoot";

/**
 * This tenant's Privacy Policy, Terms & Conditions or Account Deletion page, at
 * a stable public URL that needs no sign-in:
 *   /t/<slug>/privacy-policy   /t/<slug>/terms   /t/<slug>/account-deletion
 * The text is requested with the slug from the URL, so one tenant's page can
 * only ever show that tenant's document.
 */
export default function TenantLegal({ doc }) {
  const { slug } = useParams();
  const { tenant } = useTenant();
  const [document_, setDocument] = useState(null);
  const [error, setError] = useState(false);
  const appName = tenant.branding?.app_name || tenant.business_name;
  const navigate = useNavigate();
  // Reached from inside the app (Account, sign-in): offer the way back. iOS has
  // no system back button. Opened directly from a store listing: nothing to go back to.
  const cameFromApp = window.history.state?.idx > 0;

  useEffect(() => {
    setDocument(null); setError(false);
    api.get(`/public/tenant/${slug}/legal/${doc}`).then((r) => {
      if (r.data.tenant?.slug !== slug) throw new Error("Tenant mismatch");
      setDocument(r.data);
      document.title = r.data.heading;
    }).catch(() => setError(true));
  }, [slug, doc, appName]);

  return (
    <div className="pwa-shell pwa-shell--plain brand-body" data-testid={`tenant-legal-${doc}`}>
      <header className="brand-surface border-b brand-border">
        <div className={`pwa-container md:px-6 h-16 flex items-center gap-3 ${cameFromApp ? "px-1" : "px-5"}`}>
          {cameFromApp && (
            <button onClick={() => navigate(-1)} className="h-11 px-3 inline-flex items-center gap-1.5 text-sm font-semibold brand-text flex-shrink-0" data-testid="tenant-back-btn">
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
          )}
          <Link to={`/t/${slug}`} className="flex items-center gap-3 min-w-0">
            <TenantLogo tenant={tenant} />
            <span className="brand-heading font-bold text-lg truncate" data-testid="tenant-legal-app-name">{appName}</span>
          </Link>
        </div>
      </header>

      <main className="pwa-narrow px-5 py-6 md:py-10">
        {error && <div className="text-sm brand-text-muted" data-testid="tenant-legal-error">This document could not be loaded. Please try again.</div>}
        {!document_ && !error && <div className="text-sm brand-text-muted">Loading…</div>}
        {document_ && (
          <article>
            <h1 className="brand-heading font-bold text-2xl md:text-3xl break-words" data-testid="tenant-legal-title">{document_.heading}</h1>
            <div className="text-xs brand-text-muted mt-1">
              {appName}{document_.updated_at ? ` · Updated ${document_.updated_at.slice(0, 10)}` : ""}
            </div>
            {doc === "account-deletion" && (
              <Link to={`/t/${slug}/account/security`} data-testid="tenant-legal-delete-action"
                className="mt-5 inline-flex items-center justify-center px-6 min-h-[48px] rounded-full font-semibold text-sm border-2" style={{ borderColor: "#B91C1C", color: "#B91C1C" }}>
                Delete my {appName} account
              </Link>
            )}
            <div className="mt-6 space-y-3 text-sm leading-relaxed break-words" data-testid="tenant-legal-content">
              {document_.content.split("\n").map((line, i) => {
                const text = line.trim();
                if (!text) return null;
                if (text.startsWith("## ")) return <h2 key={i} className="brand-heading font-bold text-lg pt-4">{text.slice(3)}</h2>;
                return <p key={i}>{text}</p>;
              })}
            </div>
          </article>
        )}

        <footer className="mt-10 pt-6 border-t brand-border flex flex-wrap gap-x-5 gap-y-2 text-sm">
          <Link to={`/t/${slug}/privacy-policy`} className="underline py-2">Privacy Policy</Link>
          <Link to={`/t/${slug}/terms`} className="underline py-2">Terms & Conditions</Link>
          <Link to={`/t/${slug}/account-deletion`} className="underline py-2">Account Deletion</Link>
          <Link to={`/t/${slug}`} className="underline py-2">Back to {appName}</Link>
        </footer>
      </main>
    </div>
  );
}
